import React, { useEffect } from 'react';
import { X } from '../../bravosIcons';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';
import { toLocalDateKey } from '../../../../utils/dateKey';
import './AssignWorkoutPlanModal.css';

export type AssignWorkoutPlanModalProps = {
  open: boolean;
  assigningPlanId: string | null;
  planName: string;
  assignMembers: any[];
  loadingAssignMembers: boolean;
  selectedAssignMemberId: string;
  onSelectedAssignMemberIdChange: (id: string) => void;
  assignmentStartDate: string;
  onAssignmentStartDateChange: (date: string) => void;
  assignmentNotes: string;
  onAssignmentNotesChange: (notes: string) => void;
  assignmentError: string | null;
  isAssigning: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onAddMember: () => void;
};

function memberLabel(member: { member?: { email?: string | null } | null; memberId: string }): string {
  const profile = member.member as Parameters<typeof displayNameFromProfile>[0] | undefined;
  return displayNameFromProfile(profile) || member.member?.email || 'Unknown Member';
}

function openNativeDatePicker(input: HTMLInputElement) {
  if ('showPicker' in input && typeof input.showPicker === 'function') {
    try {
      input.showPicker();
      return;
    } catch {
      // showPicker may throw if not triggered by user gesture in some browsers
    }
  }
  input.focus();
}

/** Assign workout plan to a member (Library Plans tab). */
export function AssignWorkoutPlanModal({
  open,
  assigningPlanId,
  planName,
  assignMembers,
  loadingAssignMembers,
  selectedAssignMemberId,
  onSelectedAssignMemberIdChange,
  assignmentStartDate,
  onAssignmentStartDateChange,
  assignmentNotes,
  onAssignmentNotesChange,
  assignmentError,
  isAssigning,
  onClose,
  onSubmit,
  onAddMember,
}: AssignWorkoutPlanModalProps) {
  const todayKey = toLocalDateKey(new Date());
  const canDismiss = !isAssigning;
  const hasMembers = assignMembers.length > 0;
  const submitDisabled =
    isAssigning
    || !selectedAssignMemberId
    || !assignmentStartDate
    || !hasMembers
    || loadingAssignMembers;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canDismiss) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, canDismiss, onClose]);

  if (!open || !assigningPlanId) return null;

  return (
    <div
      className="awp-modal-scrim"
      role="presentation"
      onClick={() => {
        if (canDismiss) onClose();
      }}
    >
      <div
        className="awp-modal tr-dialog--lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="awp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="awp-modal__header">
          <h2 className="awp-modal__title" id="awp-modal-title">
            Assign to a member
          </h2>
          <button
            type="button"
            className="awp-modal__close"
            aria-label="Close"
            onClick={onClose}
            disabled={!canDismiss}
          >
            <X width={16} height={16} aria-hidden />
          </button>
        </header>

        <div className="awp-modal__body">
          {assignmentError ? (
            <div className="awp-modal__error" role="alert">
              {assignmentError}
            </div>
          ) : null}

          <div className="awp-modal__field">
            <span className="awp-modal__label">Plan</span>
            <div className="awp-modal__readonly">{planName}</div>
          </div>

          <div className="awp-modal__field">
            <label className="awp-modal__label" htmlFor="awp-member-select">
              Member
              <span className="awp-modal__required-dot" title="Required" />
            </label>
            {loadingAssignMembers ? (
              <div className="awp-modal__loading">
                <span className="awp-modal__spinner" aria-hidden="true" />
                Loading members…
              </div>
            ) : !hasMembers ? (
              <div className="awp-modal__empty">
                <p>No active members found. Add a member to assign this plan.</p>
                <button type="button" className="awp-modal__empty-cta" onClick={onAddMember}>
                  Add member
                </button>
              </div>
            ) : (
              <div className="awp-modal__select-wrap">
                <select
                  id="awp-member-select"
                  className={`awp-modal__select${selectedAssignMemberId ? ' awp-modal__select--has-value' : ''}`}
                  value={selectedAssignMemberId}
                  onChange={(e) => onSelectedAssignMemberIdChange(e.target.value)}
                  required
                >
                  <option value="">Choose a member…</option>
                  {assignMembers.map((member) => (
                    <option key={member.memberId} value={member.memberId}>
                      {memberLabel(member)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="awp-modal__field">
            <label className="awp-modal__label" htmlFor="awp-start-date">
              Start date
              <span className="awp-modal__required-dot" title="Required" />
            </label>
            <input
              id="awp-start-date"
              type="date"
              className="awp-modal__date"
              value={assignmentStartDate}
              min={todayKey}
              onChange={(e) => onAssignmentStartDateChange(e.target.value)}
              onFocus={(e) => openNativeDatePicker(e.currentTarget)}
              onClick={(e) => {
                e.stopPropagation();
                openNativeDatePicker(e.currentTarget);
              }}
              required
            />
          </div>

          <div className="awp-modal__field">
            <label className="awp-modal__label" htmlFor="awp-notes">
              Notes
              <span className="awp-modal__label-optional">— optional</span>
            </label>
            <textarea
              id="awp-notes"
              className="awp-modal__textarea"
              value={assignmentNotes}
              onChange={(e) => onAssignmentNotesChange(e.target.value)}
              placeholder="Add any notes about this assignment…"
            />
          </div>
        </div>

        <footer className="awp-modal__footer">
          <button
            type="button"
            className="awp-modal__btn awp-modal__btn--ghost"
            onClick={onClose}
            disabled={!canDismiss}
          >
            Cancel
          </button>
          <button
            type="button"
            className="awp-modal__btn awp-modal__btn--primary"
            onClick={onSubmit}
            disabled={submitDisabled}
          >
            {isAssigning ? (
              <>
                <span className="awp-modal__spinner" aria-hidden="true" />
                Assigning…
              </>
            ) : (
              'Assign plan'
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
